import {
  Avatar,
  Badge,
  Box,
  Flex,
  HStack,
  Square,
  Table,
  TableRowProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';
import { djs } from '@duke-hq/djs';
import { useCallback } from 'react';
import { constructQueryParams } from '@duke-hq/lib';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { SortOrder, SortOrderSchema } from '@duke-hq/lib/pagination';

export type MemberType = RouterOutputs['users']['listMembersWithPagination']['items'][number];

export interface SortPropsInterface {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page?: string | undefined;
}

export interface TableRowExtendedProps extends TableRowProps {
  member: MemberType;
  onMember: (id: string) => void;
}

export interface TeamMembersTableProps {
  members: MemberType[];
  urlParams: SortPropsInterface;
  onFilters: (filters: SortPropsInterface, sort: boolean) => void;
}

function TableRow({ member, onMember }: TableRowExtendedProps) {
  const consultantFullName = member.first_name + (member.last_name ? ' ' + member.last_name : '');
  const badgeRegionColor = member.totalRegions !== 0 ? 'success' : 'fg.muted';
  const badgeRegionBg = member.totalRegions !== 0 ? 'bg.success-subtle' : 'bg.subtle';
  const badgeSiteColor = member.totalSites !== 0 ? 'info' : 'fg.muted';
  const badgeSiteBg = member.totalSites !== 0 ? 'bg.info-subtle' : 'bg.subtle';

  return (
    <Tr
      key={member.id}
      onClick={() => {
        onMember(member.id);
      }}
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
    >
      <Td>
        <Flex>
          <Flex align="center" justify="center">
            <Box>
              <Square size="32px">
                <Avatar
                  size="sm"
                  name={consultantFullName}
                  src={member?.picture_url ?? undefined}
                />
              </Square>
            </Box>
          </Flex>
          <Flex direction="column" ml={4}>
            <Box>
              <Text fontWeight="medium">{consultantFullName}</Text>
            </Box>
            <Flex align="center" color="fg.muted" gap={1}>
              <Text>{member.email}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Td>
      <Td>
        <Text color="fg.muted">{member.totalEnrolments || 0}</Text>
      </Td>
      <Td>
        <HStack align="center" gap="4px">
          <Badge
            borderRadius="full"
            borderColor={badgeRegionColor}
            bg={badgeRegionBg}
            color={badgeRegionColor}
            borderWidth="1px"
            padding="0px 6px"
            sx={{ textTransform: 'none' }}
          >
            <Text fontSize="xs" fontWeight="bold">
              {member.totalRegions} Regions
            </Text>
          </Badge>
          <Badge
            borderRadius="full"
            borderColor={badgeSiteColor}
            bg={badgeSiteBg}
            color={badgeSiteColor}
            borderWidth="1px"
            padding="0px 6px"
            sx={{ textTransform: 'none' }}
          >
            <Text fontSize="xs" fontWeight="bold">
              {member.totalSites} Sites
            </Text>
          </Badge>
        </HStack>
      </Td>
      <Td>
        <Badge
          borderRadius="full"
          colorScheme={ProviderUserRoleColorSchemes[member.role] ?? 'gray'}
          variant="muted"
          textTransform="none"
        >
          {member.role.replace(/_/g, ' ')}
        </Badge>
      </Td>
      <Td>
        {member.lastActivityAt || member.last_authenticated_at ? (
          <Text color="fg.muted">
            {djs().to(djs(member.lastActivityAt || member.last_authenticated_at))}
          </Text>
        ) : (
          <Text color="fg.muted">
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
    </Tr>
  );
}

export function TeamMembersTable({ members = [], urlParams, onFilters }: TeamMembersTableProps) {
  const navigate = useNavigate();
  const { orderBy } = urlParams;
  const sort = SortOrderSchema.parse(urlParams.sort);

  const handleClick = useCallback(
    (id: string | number) => {
      const baseUrl = `/team/members/${id}`;
      navigate(`${baseUrl}${constructQueryParams({ ...urlParams })}`);
    },
    [navigate, urlParams],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        orderBy: name,
        sort: newSortDirection,
      } as SortPropsInterface;

      onFilters({ ...updatedFilters }, true);
    },
    [onFilters, sort],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Consultant`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Trans>Enrolments</Trans>
          </Th>
          <Th>
            <Trans>Location</Trans>
          </Th>
          <Th>
            <Trans>Role</Trans>
          </Th>
          <Th>
            <SortableHeader
              label={t`Last seen`}
              columnName="lastActivityAt"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
        </Tr>
      </Thead>

      <Tbody>
        {Array.isArray(members) && members.length
          ? members.map((member: MemberType) => (
              <TableRow
                key={member.id}
                member={member}
                onMember={(id: string) => {
                  handleClick(id);
                }}
              />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
